import useTranslation from "../../../client/hooks/useTranslation";
import { useLiteData } from "../../data/LiteDataProvider";
import { byKey } from "../../lib/analytics";
import { closedOnly, summarize } from "../../lib/stats";
import type { LiteTrade } from "../../lib/types";
import { BucketTable, Insights } from "../../ui/analytics";
import { SideTag } from "../../ui/TradeList";
import { Bar, Card, FirmMark, Pnl } from "../../ui/ui";
import { compact, insight, usePf, type TabProps } from "./shared";

const NONE = "__none__";

/** Un trade lié à plusieurs stratégies compte dans chacune ; sans stratégie, il va dans NONE. */
function byStrategy(trades: LiteTrade[]) {
  const map = new Map<string, LiteTrade[]>();
  for (const tr of closedOnly(trades)) {
    for (const id of tr.strategyIds.length ? tr.strategyIds : [NONE]) {
      const arr = map.get(id);
      if (arr) arr.push(tr); else map.set(id, [tr]);
    }
  }
  return [...map.entries()].map(([key, ts]) => ({ key, s: summarize(ts) })).sort((a, b) => b.s.netPnl - a.s.netPnl);
}

export function InstrumentsTab({ trades }: TabProps) {
  const tl = useTranslation().lite;
  const t = tl.stats.instruments;
  const { fmt, strategies, accountsById } = useLiteData();
  const pf = usePf();

  const symbols = byKey(trades, tr => tr.symbol).map(b => ({ ...b, label: <span className="font-semibold">{b.key}</span> }));
  const strats = byStrategy(trades).flatMap(b => {
    if (b.key === NONE) return [{ ...b, name: t.noStrategy, label: <span className="text-muted-foreground">{t.noStrategy}</span> }];
    const st = strategies.find(x => x.id === b.key);
    if (!st) return [];
    return [{
      ...b,
      name: st.name,
      label: <span className="inline-flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full" style={{ background: st.color }} />{st.name}</span>,
    }];
  });
  const accounts = byKey(trades, tr => tr.accountId ?? NONE).map(b => {
    const acc = b.key === NONE ? undefined : accountsById.get(b.key);
    const name = b.key === NONE ? t.noAccount : acc?.name ?? t.unknownAccount;
    return { ...b, label: <span className="inline-flex items-center gap-2"><FirmMark firm={acc?.firm ?? null} size="sm" />{name}</span> };
  });
  const sides = (["long", "short"] as const).map(side => ({ side, s: summarize(trades.filter(tr => tr.side === side)) }));
  const sideSymbols = symbols.map(sym => ({
    sym: sym.key,
    long: summarize(trades.filter(tr => tr.symbol === sym.key && tr.side === "long")),
    short: summarize(trades.filter(tr => tr.symbol === sym.key && tr.side === "short")),
  }));

  const best = symbols[0];
  const worst = symbols.length > 1 ? symbols[symbols.length - 1] : undefined;
  const bestStrat = strats.find(b => b.key !== NONE);
  const [longS, shortS] = [sides[0].s, sides[1].s];
  const longsBetter = longS.netPnl >= shortS.netPnl;

  const insights = compact([
    best && insight("good", t.insightBest(best.key, fmt.money(best.s.netPnl), best.s.count)),
    worst && worst.s.netPnl < 0 && insight("bad", t.insightWorst(worst.key, fmt.money(worst.s.netPnl, { sign: false }), fmt.pct(worst.s.winRate))),
    bestStrat && insight("info", t.insightBestStrat(bestStrat.name, pf(bestStrat.s.profitFactor))),
    longS.count > 0 && shortS.count > 0 && insight("info", t.insightSides(
      longsBetter,
      fmt.money(Math.max(longS.netPnl, shortS.netPnl)),
      fmt.money(Math.min(longS.netPnl, shortS.netPnl)),
    )),
  ]);

  const cell = (s: ReturnType<typeof summarize>) => (s.count ? t.rateAndCount(fmt.pct(s.winRate), s.count) : tl.stats.none);

  return (
    <>
      <Insights items={insights} />
      <div className="grid xl:grid-cols-2 gap-5">
        <BucketTable title={t.bySymbol} rows={symbols} />
        <BucketTable title={t.byStrategy} subtitle={t.byStrategySubtitle} rows={strats} />
      </div>
      <div className="grid xl:grid-cols-12 gap-5">
        <Card title={t.sides} className="xl:col-span-4">
          <div className="space-y-5">
            {sides.map(({ side, s }) => (
              <div key={side}>
                <div className="flex items-center justify-between"><SideTag side={side} /><Pnl value={s.netPnl} className="font-semibold" /></div>
                <div className="flex justify-between text-xs text-muted-foreground mt-2 mb-1.5">
                  <span>{t.sideMeta(s.count, pf(s.profitFactor))}</span>
                  <span className="font-mono tabular-nums">{t.sideWinners(fmt.pct(s.winRate))}</span>
                </div>
                <Bar value={s.winRate} tone={s.winRate >= 0.5 ? "gain" : "warn"} />
              </div>
            ))}
          </div>
        </Card>
        <Card title={t.sidesBySymbol} className="xl:col-span-8" pad={false}>
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-sm min-w-[480px]">
              <thead>
                <tr className="text-[11px] text-muted-foreground/70 border-y border-border">
                  <th className="text-left font-normal pl-5 py-2" />
                  <th className="text-right font-normal px-3 py-2">{t.longWinners}</th>
                  <th className="text-right font-normal px-3 py-2">{t.long}</th>
                  <th className="text-right font-normal px-3 py-2">{t.shortWinners}</th>
                  <th className="text-right font-normal pr-5 py-2">{t.short}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sideSymbols.map(r => (
                  <tr key={r.sym}>
                    <td className="pl-5 py-2.5 font-semibold">{r.sym}</td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums text-muted-foreground">{cell(r.long)}</td>
                    <td className="px-3 py-2.5 text-right"><Pnl value={r.long.netPnl} /></td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums text-muted-foreground">{cell(r.short)}</td>
                    <td className="pr-5 py-2.5 text-right"><Pnl value={r.short.netPnl} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
      <BucketTable title={t.byAccount} rows={accounts} />
    </>
  );
}
